import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies for JWT auth
});

// Add user ID to every request (legacy support)
api.interceptors.request.use((config) => {
  const userId = localStorage.getItem("navi_user_id");
  if (userId) {
    config.headers["x-user-id"] = userId;
  }
  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not a retry, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await api.post("/auth/refresh");
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, user needs to login again
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================
// SECURE AUTH (Email/Password with JWT)
// ============================================================
export const authRegister = (email, password, name, role = "teacher") =>
  api.post("/auth/register", { email, password, name, role });

export const authLogin = (email, password) =>
  api.post("/auth/login/secure", { email, password });

export const authLogout = () =>
  api.post("/auth/logout");

export const authGetMe = () =>
  api.get("/auth/me/secure");

export const authRefresh = () =>
  api.post("/auth/refresh");

// ============================================================
// ADMIN
// ============================================================
export const adminListUsers = () =>
  api.get("/admin/users");

export const adminDeleteUser = (userId) =>
  api.delete(`/admin/users/${userId}`);

export const adminUpdateRole = (userId, role) =>
  api.put(`/admin/users/${userId}/role?role=${role}`);

export const adminValidateUser = (userId) =>
  api.put(`/admin/users/${userId}/validate`);

export const adminInvalidateUser = (userId) =>
  api.put(`/admin/users/${userId}/invalidate`);

// ============================================================
// LEGACY AUTH (Name-based, backwards compatible)
// ============================================================
export const login = (name, role, niveau = null) => 
  api.post("/auth/login", { name, role, niveau });
export const getMe = () => api.get("/auth/me");

// Text analysis
export const analyzeText = (text, profileId) =>
  api.post("/analyze", { text, profile_id: profileId });

// Text documents
export const createText = (title, rawText, profileId) =>
  api.post("/texts", { title, raw_text: rawText, profile_id: profileId });
export const listTexts = () => api.get("/texts");
export const getText = (id) => api.get(`/texts/${id}`);
export const updateText = (id, data) => api.put(`/texts/${id}`, data);
export const deleteText = (id) => api.delete(`/texts/${id}`);

// Profiles
export const listProfiles = () => api.get("/profiles");
export const listSystemProfiles = () => api.get("/profiles/system");
export const createProfile = (data) => api.post("/profiles", data);
export const updateProfile = (id, data) => api.put(`/profiles/${id}`, data);
export const deleteProfile = (id) => api.delete(`/profiles/${id}`);

// Sample texts
export const getSampleTexts = () => api.get("/sample-texts");

// TTS (Text-to-Speech)
export const getTTSStatus = () => api.get("/tts/status");
export const synthesizeSpeech = (text, speed = 1.0) =>
  api.post("/tts", { text, speed }, { responseType: "blob" });

// PDF Export
export const exportPDF = (text, title, profileId) =>
  api.post("/export/pdf", { text, title, profile_id: profileId }, { responseType: "blob" });

// Students
export const listStudents = () => api.get("/students");
export const createStudent = (data) => api.post("/students", data);
export const getStudent = (id) => api.get(`/students/${id}`);
export const updateStudent = (id, data) => api.put(`/students/${id}`, data);
export const deleteStudent = (id) => api.delete(`/students/${id}`);
export const addStudentProgress = (id, data) => api.post(`/students/${id}/progress`, data);

// Parents
export const listParents = () => api.get("/parents");
export const createParent = (data) => api.post("/parents", data);
export const deleteParent = (id) => api.delete(`/parents/${id}`);

// Login by access code (for students/parents)
export const loginByCode = (accessCode) => 
  api.post("/auth/login-code", null, { params: { access_code: accessCode } });

// Creative Documents
export const listDocuments = () => api.get("/documents");
export const createDocument = (data) => api.post("/documents", data);
export const getDocument = (id) => api.get(`/documents/${id}`);
export const updateDocument = (id, data) => api.put(`/documents/${id}`, data);
export const deleteDocument = (id) => api.delete(`/documents/${id}`);

// Export Document as PDF
export const exportDocumentPDF = (imageData, title, orientation = "portrait") =>
  api.post("/documents/export-pdf", { image_data: imageData, title, orientation }, { responseType: "blob" });

// Export Document as Grouped PDF (2, 4, 8, 16 per page)
export const exportDocumentPDFGrouped = (images, title, grouping = 1, orientation = "portrait") =>
  api.post("/documents/export-pdf-grouped", { images, title, grouping, orientation }, { responseType: "blob" });

// Cliparts
export const listCliparts = () => api.get("/cliparts");

// AI Generation
export const aiGenerateText = (data) => api.post("/ai/generate-text", data);
export const aiGenerateExercise = (data) => api.post("/ai/generate-exercise", data);
export const aiChat = (message, context = null, history = null) => 
  api.post("/ai/chat", { message, context, conversation_history: history });
export const aiSuggest = (niveau, subject, count = 5) => 
  api.post("/ai/suggest", { niveau, subject, count });

// Reading Sessions (Timer)
export const createReadingSession = (data) => api.post("/reading-sessions", data);
export const listReadingSessions = () => api.get("/reading-sessions");
export const getStudentSessions = (studentId) => api.get(`/reading-sessions/by-student/${studentId}`);

// Rewards
export const getRewards = () => api.get("/rewards");

// Pictograms
export const getPictograms = () => api.get("/pictograms");

// Batch PDF
export const exportPDFBatch = (text, title, profileId, studentNames) =>
  api.post("/export/pdf-batch", { text, title, profile_id: profileId, student_names: studentNames }, { responseType: "blob" });

// Class Stats
export const getClassStats = () => api.get("/class-stats");

// Text Sharing
export const shareText = (id) => api.post(`/texts/${id}/share`);
export const unshareText = (id) => api.post(`/texts/${id}/unshare`);
export const listSharedTexts = () => api.get("/texts/shared/all");

// Dictation
export const checkDictation = (original, typed) => api.post("/dictation/check", { original, typed });

// Enhanced Dictation
export const getDictationTTS = (text, options = {}) =>
  api.post("/dictation/tts", {
    text,
    slow_mode: options.slowMode ?? true,
    announce_punctuation: options.announcePunctuation ?? true,
    pause_between_sentences: options.pauseBetweenSentences ?? true,
  }, { responseType: "blob" });

export const uploadTeacherAudio = (textId) =>
  api.post("/dictation/upload-audio", null, { params: { text_id: textId } });

export const uploadAudioFile = (audioId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/dictation/upload-audio-file/${audioId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getTeacherAudio = (textId) => api.get(`/dictation/teacher-audio/${textId}`);

export const getAudioFile = (audioId) =>
  api.get(`/dictation/audio-file/${audioId}`, { responseType: "blob" });

// Grammar Module
export const analyzeGrammar = (text, level = "intermediate") =>
  api.post("/grammar/analyze", { text, level });

export const getGrammarExercise = (text, exerciseType, level = "intermediate") =>
  api.post("/grammar/exercise", { text, exercise_type: exerciseType, level });

export const getDictationHints = (text, level = "intermediate") =>
  api.post("/grammar/dictation-hints", { text, level });

// Parent Mode
export const generateShareLink = (studentId) =>
  api.post(`/students/${studentId}/share-link`);

export const sendParentEmail = (studentId, parentEmail, shareToken) =>
  api.post(`/students/${studentId}/send-parent-email`, { 
    student_id: studentId, 
    parent_email: parentEmail, 
    share_token: shareToken 
  });

// Math Module
export const generateMathExercise = (exerciseType, niveau, difficulty = 1) =>
  api.post("/math/generate", { exercise_type: exerciseType, niveau, difficulty });

export const saveMathResult = (exerciseId, correct, timeSeconds, answerGiven, studentId = null) =>
  api.post("/math/result", { 
    exercise_id: exerciseId, 
    correct, 
    time_seconds: timeSeconds, 
    answer_given: answerGiven,
    student_id: studentId 
  });

// English Module
export const getEnglishCategories = () => api.get("/english/categories");
export const getEnglishVocabulary = (category) => api.get(`/english/vocabulary/${category}`);
export const generateEnglishExercise = (exerciseType, niveau, category = null) =>
  api.post("/english/exercise", { exercise_type: exerciseType, niveau, category });

export const saveEnglishResult = (exerciseId, correct, timeSeconds, answerGiven, studentId = null) =>
  api.post("/english/result", { 
    exercise_id: exerciseId, 
    correct, 
    time_seconds: timeSeconds, 
    answer_given: answerGiven,
    student_id: studentId 
  });

// ============================================================
// RESOURCES API (Unified resource management)
// ============================================================

export const getMetadataOptions = () => api.get("/resources/metadata-options");

export const createResource = (data) => api.post("/resources", data);

export const listResources = (params = {}) => api.get("/resources", { params });

export const getResource = (id) => api.get(`/resources/${id}`);

export const updateResource = (id, data) => api.put(`/resources/${id}`, data);

export const shareResource = (id) => api.post(`/resources/${id}/share`);

export const unshareResource = (id) => api.post(`/resources/${id}/unshare`);

export const duplicateResource = (id) => api.post(`/resources/${id}/duplicate`);

export const deleteResource = (id) => api.delete(`/resources/${id}`);

// Resource variants
export const getResourceVariants = (resourceId) => 
  api.get(`/resources/${resourceId}/variants`);

export const generateResourceVariants = (resourceId, studentIds) =>
  api.post(`/resources/${resourceId}/generate-variants`, null, { 
    params: { student_ids: studentIds } 
  });

export const deleteResourceVariant = (resourceId, variantId) =>
  api.delete(`/resources/${resourceId}/variants/${variantId}`);

export const regenerateVariant = (variantId) =>
  api.post(`/resources/variants/${variantId}/regenerate`);

// Shared resources
export const getSharedResources = (params = {}) => 
  api.get("/resources/shared/all", { params });

// Create from session
export const createResourceFromSession = (sessionId) =>
  api.post(`/resources/from-session/${sessionId}`);

// ============================================================
// JOURNAL API (Cahier-journal) - Legacy + New Sessions API
// ============================================================

// Legacy journal API (for backward compatibility)
export const createJournalEntry = (data) => api.post("/journal", data);

export const listJournalEntries = (params = {}) => 
  api.get("/journal", { params });

export const getWeekJournal = (dateStr) => 
  api.get(`/journal/week/${dateStr}`);

export const getTodayJournal = () => api.get("/journal/today");

export const getJournalEntry = (id) => api.get(`/journal/${id}`);

export const updateJournalEntry = (id, data) => api.put(`/journal/${id}`, data);

export const updateJournalStatus = (id, status) => 
  api.post(`/journal/${id}/status/${status}`);

export const deleteJournalEntry = (id) => api.delete(`/journal/${id}`);

// New Advanced Sessions API
export const getTimeSlots = () => api.get("/journal/time-slots");

export const updateTimeSlots = (slots) => 
  api.put("/journal/time-slots", { slots });

export const resetTimeSlots = () => api.post("/journal/time-slots/reset");

export const createSession = (data) => api.post("/journal/sessions", data);

export const listSessions = (params = {}) => 
  api.get("/journal/sessions", { params });

export const getWeekSessions = (dateStr) => 
  api.get(`/journal/sessions/week/${dateStr}`);

export const getSessionsForPeriod = (startDate, endDate) =>
  api.get("/journal/sessions/period", { params: { start_date: startDate, end_date: endDate } });

export const getTodaySessions = () => api.get("/journal/sessions/today");

export const getDaySessions = (dateStr) => api.get(`/journal/sessions/day/${dateStr}`);

export const getSession = (id) => api.get(`/journal/sessions/${id}`);

export const updateSession = (id, data) => api.put(`/journal/sessions/${id}`, data);

export const updateSessionStatus = (id, status) => 
  api.post(`/journal/sessions/${id}/status/${status}`);

export const deleteSession = (id) => api.delete(`/journal/sessions/${id}`);

export const duplicateSession = (id, newDate = null) => 
  api.post(`/journal/sessions/${id}/duplicate`, null, { params: { new_date: newDate } });

export const moveSession = (id, data) =>
  api.put(`/journal/sessions/${id}/move`, data);

export const resizeSession = (id, data) =>
  api.put(`/journal/sessions/${id}/resize`, data);

export const generateSessionVariants = (sessionId) =>
  api.post(`/journal/sessions/${sessionId}/generate-variants`);

export const suggestSkillsForSession = (sessionId) =>
  api.get(`/journal/sessions/${sessionId}/suggest-skills`);

// Journal Stats
export const getSkillsCoverage = (params = {}) => 
  api.get("/journal/stats/skills-coverage", { params });

export const getWeeklySummary = (dateStr) =>
  api.get(`/journal/stats/weekly-summary`, { params: { date_str: dateStr } });

// Timetable (Emploi du temps)
export const getTimetable = () => api.get("/journal/timetable");

export const saveTimetable = (data) => api.put("/journal/timetable", data);

// ============================================================
// SKILLS API (Official Skills Repository - SSOT)
// ============================================================

export const getSkillsRepositories = () => api.get("/skills/repositories");

export const getSkillsRepository = (id) => api.get(`/skills/repositories/${id}`);

export const searchSkills = (params = {}) => api.get("/skills/items", { params });

export const getSkill = (id) => api.get(`/skills/items/${id}`);

export const getSkillChildren = (id) => api.get(`/skills/items/${id}/children`);

export const getSkillsDomains = (params = {}) => api.get("/skills/domains", { params });

export const getSkillsSubdomains = (params = {}) => api.get("/skills/subdomains", { params });

export const getSkillsCategories = () => api.get("/skills/categories");

export const getSkillsStats = () => api.get("/skills/stats");

// ============================================================
// SCHEDULE API (Week configuration, timetables, calendar)
// ============================================================

// Week Schedule Configuration
export const getWeekTypes = () => api.get("/schedule/week-types");

export const getWeekSchedule = () => api.get("/schedule/week-schedule");

export const updateWeekSchedule = (config) => api.put("/schedule/week-schedule", config);

// Timetables (Emploi du temps)
export const listTimetables = () => api.get("/schedule/timetables");

export const getActiveTimetable = () => api.get("/schedule/timetables/active");

export const createTimetable = (data) => api.post("/schedule/timetables", data);

export const updateTimetable = (id, data) => api.put(`/schedule/timetables/${id}`, data);

export const deleteTimetable = (id) => api.delete(`/schedule/timetables/${id}`);

export const activateTimetable = (id) => api.post(`/schedule/timetables/${id}/activate`);

// Academic Zones & Holidays
export const getAcademicZones = () => api.get("/schedule/zones");

export const getTeacherZone = () => api.get("/schedule/teacher-zone");

export const updateTeacherZone = (data) => api.put("/schedule/teacher-zone", data);

export const getHolidays = (zone = "B", year = null) => 
  api.get("/schedule/holidays", { params: { zone, year } });

export const checkWorkingDay = (dateStr, zone = "B") =>
  api.get("/schedule/is-working-day", { params: { date_str: dateStr, zone } });

// Hourly Distribution (Répartition horaire)
export const getOfficialDistribution = (cycle = "cycle2") =>
  api.get("/schedule/hourly-distribution/official", { params: { cycle } });

export const getActualDistribution = (startDate, endDate) =>
  api.get("/schedule/hourly-distribution/actual", { params: { start_date: startDate, end_date: endDate } });

export const compareDistribution = (cycle = "cycle2", startDate, endDate) =>
  api.get("/schedule/hourly-distribution/comparison", { params: { cycle, start_date: startDate, end_date: endDate } });

// Session Generation
export const generateSessionsFromTimetable = (startDate, endDate, zone = "B") =>
  api.post("/schedule/generate-sessions", null, { params: { start_date: startDate, end_date: endDate, zone } });

export const getWeekParity = (dateStr) =>
  api.get("/schedule/week-parity", { params: { date_str: dateStr } });

// Extended Skills Repositories (Seeded data)
export const listExtendedRepositories = () => api.get("/skills/extended-repositories");

export const getRepositorySkills = (repoId, params = {}) =>
  api.get(`/skills/extended-repositories/${repoId}/skills`, { params });

export const getRepositoryHierarchy = (repoId) =>
  api.get(`/skills/extended-repositories/${repoId}/hierarchy`);

// Journal Export (PDF)
export const exportDayPdf = (dateStr) => 
  api.get(`/journal-export/day/${dateStr}`, { responseType: 'blob' });

export const exportWeekPdf = (dateStr) =>
  api.get(`/journal-export/week/${dateStr}`, { responseType: 'blob' });

// New explicit exports for Semainier vs Cahier-journal
export const exportSemainierPdf = (dateStr) =>
  api.get(`/journal-export/semainier/${dateStr}`, { responseType: 'blob' });

export const exportCahierJournalDayPdf = (dateStr) =>
  api.get(`/journal-export/cahier-journal/day/${dateStr}`, { responseType: 'blob' });

export const exportCahierJournalWeekPdf = (dateStr) =>
  api.get(`/journal-export/cahier-journal/week/${dateStr}`, { responseType: 'blob' });

// ============================================================
// VARIANTS API (Student differentiation)
// ============================================================

export const getDisplayPreferencesDefaults = () => 
  api.get("/variants/display-preferences-defaults");

export const generateVariants = (resourceId, studentIds, options = {}) =>
  api.post(`/variants/generate/${resourceId}`, {
    student_ids: studentIds,
    apply_reading_profile: options.applyReadingProfile ?? true,
    apply_display_preferences: options.applyDisplayPreferences ?? true,
  });

export const listResourceVariants = (resourceId) =>
  api.get(`/variants/resource/${resourceId}`);

export const getVariant = (variantId) => api.get(`/variants/${variantId}`);

export const getStudentResources = (studentId) =>
  api.get(`/variants/student/${studentId}/resources`);

export const deleteVariant = (variantId) => api.delete(`/variants/${variantId}`);

// ============================================================
// JOURNAL EVENTS API (Phase 3 - Événements)
// ============================================================

export const getEventTypes = () => api.get("/journal-events/types");

export const listEvents = (params = {}) => 
  api.get("/journal-events/", { params });

export const getWeekEvents = (dateStr) => 
  api.get(`/journal-events/week/${dateStr}`);

export const getUpcomingEvents = (days = 14) => 
  api.get("/journal-events/upcoming", { params: { days } });

export const getEventReminders = () => 
  api.get("/journal-events/reminders");

export const getEvent = (eventId) => 
  api.get(`/journal-events/${eventId}`);

export const createEvent = (data) => 
  api.post("/journal-events/", data);

export const updateEvent = (eventId, data) => 
  api.put(`/journal-events/${eventId}`, data);

export const deleteEvent = (eventId, deleteRecurring = false) => 
  api.delete(`/journal-events/${eventId}`, { params: { delete_recurring: deleteRecurring } });

export const updateEventStatus = (eventId, status) => 
  api.post(`/journal-events/${eventId}/status`, null, { params: { status } });

export const duplicateEvent = (eventId, newDate) => 
  api.post(`/journal-events/${eventId}/duplicate`, null, { params: { new_date: newDate } });

// ============================================================
// SUBJECTS API (Dynamic subjects by cycle/level)
// ============================================================

export const getSubjectCatalogs = () => api.get("/subjects/catalogs");

export const getSubjectCatalog = (cycleId) => api.get(`/subjects/catalogs/${cycleId}`);

export const getSubjectsForNiveau = (niveau) => api.get(`/subjects/for-niveau/${niveau}`);

export const getSubjectsForCycles = (cycles) => 
  api.get("/subjects/for-cycles", { params: { cycles: cycles.join(",") } });

export const getHoursReference = (cycleId) => api.get(`/subjects/hours-reference/${cycleId}`);

export const createCustomSubject = (data) => api.post("/subjects/custom", data);

export const updateCustomSubject = (id, data) => api.put(`/subjects/custom/${id}`, data);

export const deleteCustomSubject = (id) => api.delete(`/subjects/custom/${id}`);

export const listCustomSubjects = () => api.get("/subjects/custom");

// ============================================================
// ATTENDANCE API (Appel / Registres)
// ============================================================

export const saveDailyAttendance = (data) => api.post("/attendance/daily", data);

export const getDailyAttendance = (dateStr) => api.get(`/attendance/daily/${dateStr}`);

export const getWeekAttendance = (startDate) => api.get(`/attendance/week/${startDate}`);

export const saveSessionAttendance = (data) => api.post("/attendance/session", data);

export const getSessionAttendance = (sessionId) => api.get(`/attendance/session/${sessionId}`);

export const listAbsences = (params = {}) => api.get("/attendance/absences", { params });

export const justifyAbsence = (dateStr, studentId, reason, reasonDetails = null, documentUrl = null) =>
  api.put(`/attendance/absences/${dateStr}/${studentId}/justify`, null, { 
    params: { reason, reason_details: reasonDetails, document_url: documentUrl } 
  });

export const updateStudentRegistry = (studentId, data) => 
  api.put(`/attendance/students/${studentId}/registry`, data);

export const getStudentRegistry = (studentId) => 
  api.get(`/attendance/students/${studentId}/registry`);

export const getCantineList = (dateStr) => api.get(`/attendance/cantine/${dateStr}`);

export const getCantineSummary = () => api.get("/attendance/cantine/summary");

export const getFieldTripMedications = () => api.get("/attendance/field-trip-medications");

export const getAttendanceStats = (params = {}) => api.get("/attendance/stats", { params });

export const getStudentAttendanceStats = (studentId) => 
  api.get(`/attendance/stats/student/${studentId}`);

export const getAbsenceReasons = () => api.get("/attendance/absence-reasons");

// ============================================================
// LESSON PLANS API (Fiches de prép)
// ============================================================

export const createLessonPlan = (data) => api.post("/lesson-plans", data);

export const listLessonPlans = (params = {}) => api.get("/lesson-plans", { params });

export const getLessonPlan = (id) => api.get(`/lesson-plans/${id}`);

export const updateLessonPlan = (id, data) => api.put(`/lesson-plans/${id}`, data);

export const deleteLessonPlan = (id) => api.delete(`/lesson-plans/${id}`);

export const duplicateLessonPlan = (id) => api.post(`/lesson-plans/${id}/duplicate`);

export const linkLessonPlanToSession = (planId, sessionId) => 
  api.post(`/lesson-plans/${planId}/link-session/${sessionId}`);

export const unlinkLessonPlanFromSession = (planId, sessionId) =>
  api.delete(`/lesson-plans/${planId}/unlink-session/${sessionId}`);

export const generateLessonPlanWithAI = (data) => api.post("/lesson-plans/generate", data);

export const getLessonPlanTemplates = (params = {}) => 
  api.get("/lesson-plans/templates", { params });

export const createLessonPlanFromTemplate = (templateId, title, niveau, domainLabel) =>
  api.post(`/lesson-plans/from-template/${templateId}`, null, { 
    params: { title, niveau, domain_label: domainLabel } 
  });

// ============================================================
// CARTABLE API (École-Famille Communication)
// ============================================================

// Carnet de liaison
export const createLiaisonMessage = (data) => api.post("/cartable/messages", data);

export const listLiaisonMessages = (params = {}) => api.get("/cartable/messages", { params });

export const getLiaisonMessage = (id) => api.get(`/cartable/messages/${id}`);

export const updateLiaisonMessage = (id, data) => api.put(`/cartable/messages/${id}`, data);

export const deleteLiaisonMessage = (id) => api.delete(`/cartable/messages/${id}`);

export const publishLiaisonMessage = (id) => api.post(`/cartable/messages/${id}/publish`);

export const signLiaisonMessage = (messageId, studentId, signerName) =>
  api.post(`/cartable/messages/${messageId}/sign`, null, { 
    params: { student_id: studentId, signer_name: signerName } 
  });

export const getMessageSignatures = (messageId) => 
  api.get(`/cartable/messages/${messageId}/signatures`);

// Cahier de texte (Devoirs)
export const createHomework = (data) => api.post("/cartable/homework", data);

export const listHomework = (params = {}) => api.get("/cartable/homework", { params });

export const getHomework = (id) => api.get(`/cartable/homework/${id}`);

export const updateHomework = (id, data) => api.put(`/cartable/homework/${id}`, data);

export const deleteHomework = (id) => api.delete(`/cartable/homework/${id}`);

export const markHomeworkComplete = (homeworkId, studentId, completed = true) =>
  api.post(`/cartable/homework/${homeworkId}/complete`, null, { 
    params: { student_id: studentId, completed } 
  });

export const getHomeworkByDate = (dateStr) => api.get(`/cartable/homework/by-date/${dateStr}`);

export const getHomeworkForWeek = (startDate) => api.get(`/cartable/homework/week/${startDate}`);

export const getCartableSummary = () => api.get("/cartable/summary");

// Cahier de vie (Class Life)
export const getLifeEntryTypes = () => api.get("/cartable/life-entry-types");

export const createLifeEntry = (data) => api.post("/cartable/life", data);

export const listLifeEntries = (params = {}) => api.get("/cartable/life", { params });

export const getLifeEntry = (id) => api.get(`/cartable/life/${id}`);

export const updateLifeEntry = (id, data) => api.put(`/cartable/life/${id}`, data);

export const deleteLifeEntry = (id) => api.delete(`/cartable/life/${id}`);

export const publishLifeEntry = (id) => api.post(`/cartable/life/${id}/publish`);

export const toggleLikeEntry = (id) => api.post(`/cartable/life/${id}/like`);

export const addLifeComment = (entryId, text) => 
  api.post(`/cartable/life/${entryId}/comment`, null, { params: { text } });

// Suivi élèves (Student Tracking)
export const getStudentNoteTypes = () => api.get("/cartable/student-note-types");

export const createStudentNote = (data) => api.post("/cartable/student-notes", data);

export const listStudentNotes = (params = {}) => api.get("/cartable/student-notes", { params });

export const getStudentNote = (id) => api.get(`/cartable/student-notes/${id}`);

export const updateStudentNote = (id, data) => api.put(`/cartable/student-notes/${id}`, data);

export const deleteStudentNote = (id) => api.delete(`/cartable/student-notes/${id}`);

export const getStudentNotesByStudent = (studentId) => 
  api.get(`/cartable/student-notes/by-student/${studentId}`);

// Réunions parents (Parent Meetings)
export const createMeeting = (data) => api.post("/cartable/meetings", data);

export const listMeetings = (params = {}) => api.get("/cartable/meetings", { params });

export const getMeeting = (id) => api.get(`/cartable/meetings/${id}`);

export const updateMeeting = (id, data) => api.put(`/cartable/meetings/${id}`, data);

export const deleteMeeting = (id) => api.delete(`/cartable/meetings/${id}`);

export const updateMeetingStatus = (meetingId, status) => 
  api.post(`/cartable/meetings/${meetingId}/status`, null, { params: { status } });

export const getMeetingsCalendar = (month) => api.get(`/cartable/meetings/calendar/${month}`);

// ============================================================
// STORAGE API (File Upload/Download)
// ============================================================

export const uploadFile = (file, folder = "general") => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/storage/upload?folder=${folder}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const uploadMultipleFiles = (files, folder = "general") => {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  return api.post(`/storage/upload-multiple?folder=${folder}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const listFiles = (params = {}) => api.get("/storage/list", { params });

export const getFileInfo = (fileId) => api.get(`/storage/info/${fileId}`);

export const deleteFile = (fileId) => api.delete(`/storage/files/${fileId}`);

export const getFileUrl = (fileId) => {
  const userId = localStorage.getItem("navi_user_id");
  return `${BACKEND_URL}/api/storage/files/${fileId}?auth=${userId}`;
};

// ============================================================
// ALBUMS / LIVRE PHOTO API
// ============================================================

export const createAlbum = (data) => api.post("/cartable/albums", data);

export const listAlbums = (params = {}) => api.get("/cartable/albums", { params });

export const getAlbum = (albumId) => api.get(`/cartable/albums/${albumId}`);

export const updateAlbum = (albumId, data) => api.put(`/cartable/albums/${albumId}`, data);

export const deleteAlbum = (albumId) => api.delete(`/cartable/albums/${albumId}`);

export const publishAlbum = (albumId) => api.post(`/cartable/albums/${albumId}/publish`);

export const addPhotoToAlbum = (albumId, data) => api.post(`/cartable/albums/${albumId}/photos`, data);

export const removePhotoFromAlbum = (albumId, photoId) => 
  api.delete(`/cartable/albums/${albumId}/photos/${photoId}`);

export const updatePhotoCaption = (albumId, photoId, caption) =>
  api.put(`/cartable/albums/${albumId}/photos/${photoId}`, null, { params: { caption } });

// ============================================================
// CODES FAMILLE API
// ============================================================

export const generateFamilyCode = (studentId) => 
  api.post("/cartable/family-codes/generate", null, { params: { student_id: studentId } });

export const listFamilyCodes = () => api.get("/cartable/family-codes");

export const revokeFamilyCode = (codeId) => api.delete(`/cartable/family-codes/${codeId}`);

export const regenerateFamilyCode = (codeId) => api.post(`/cartable/family-codes/regenerate/${codeId}`);

export const loginWithFamilyCode = (code) => 
  api.post("/cartable/family-codes/login", null, { params: { code } });

export const getFamilyDashboard = () => api.get("/cartable/family/dashboard");

export default api;
